// src/rms/data/rentalService.js
// ============================================================================
// The rental engine
// ============================================================================
// Every vertical's workflow runs through these functions. They read the
// operator's resolved vertical config to decide what is required, but they
// never branch on a specific vertical key — the two structurally different
// verticals are handled by occupancyModel and by workflow flags
// (certificationRequired, deliveryEnabled, accessControlTracking), both of
// which are data.
//
// Returns the repo-standard { success, data, error } shape throughout.
// ============================================================================

import db from "./index";
import { createSuccessResponse, createErrorResponse } from "../utils/response";
import { resolveConfig, OCCUPANCY_CONTINUOUS } from "../verticals/presets";
import rateEngine, {
  billableUnits,
  buildQuote,
  reservationDepositCents,
  usageChargeCents,
  addonsForAsset,
  addonAmountCents,
} from "../pricing/rateEngine";
import availability, { availabilityConflict, allocateFromGroup, utilizationPct } from "../pricing/availability";
import { applyTax } from "../pricing/taxEngine";
import { loadTaxConfig } from "./taxService";
import glService from "./glService";
import { assetLabel } from "../utils/assetLabel";
import { sendTemplated } from "./messageService";
import { getLocale } from "../i18n/locale";

/** Dates for an email: readable to a customer, unambiguous about the day. */
const formatWhen = (value) => {
  if (!value) return "";
  try {
    return new Date(value).toLocaleString(getLocale(), {
      weekday: "short", day: "numeric", month: "short",
      hour: "numeric", minute: "2-digit",
    });
  } catch {
    return String(value);
  }
};

const formatCents = (cents, currency = "USD") => {
  try {
    return new Intl.NumberFormat(getLocale(), { style: "currency", currency }).format((cents || 0) / 100);
  } catch {
    return `${((cents || 0) / 100).toFixed(2)} ${currency}`;
  }
};

const nowIso = () => new Date().toISOString();

/**
 * The tax fields for a line of a given kind.
 *
 * `tax_group_id` is what the engine reads. `taxable` is kept alongside it
 * because it is real operator data on rms_addons, it is on every invoice line
 * already written, and there is a checkbox for it on the pricing screen -- but
 * the group is authoritative, so the two can never disagree.
 */
const taxOn = (taxConfig, kind) => {
  const groupId = taxConfig?.groupFor ? taxConfig.groupFor(kind) : null;
  return { tax_group_id: groupId, taxable: groupId !== null };
};

/** Resolved config for an operator record. */
export function configFor(operator) {
  if (!operator) return null;
  return resolveConfig(operator.vertical_key, operator.config_overrides || {});
}

export { billableUnits };

/**
 * Full quote for an asset and window, with the operator's rate rules applied.
 *
 * Async because rules, group and fleet utilisation all come from the store.
 * quoteReservationSync below is the pure version for callers that already have
 * the data in hand (the booking form previews with it on every keystroke).
 */
export async function quoteReservation(operator, asset, startsAt, endsAt) {
  const rules = (await db.list("rateRules", { operator_id: operator.id })).data || [];
  const group = asset.group_id ? (await db.get("assetGroups", asset.group_id)).data : null;
  const assets = (await db.list("assets", { operator_id: operator.id })).data || [];
  const reservations = (await db.list("reservations", { operator_id: operator.id })).data || [];

  return buildQuote({
    asset,
    group,
    rules,
    startsAt,
    endsAt,
    utilizationPct: utilizationPct({ assets, reservations, startsAt, endsAt }),
  });
}

/** Pure quote for callers that already hold rules and group. */
export function quoteReservationSync({ asset, group, rules, startsAt, endsAt, utilization }) {
  return buildQuote({ asset, group, rules, startsAt, endsAt, utilizationPct: utilization });
}

// ----------------------------------------------------------------------------
// Reservations
// ----------------------------------------------------------------------------

/**
 * `payload.created_signature_id` is a PENDING signature minted moments earlier
 * by rms_sign_action. The trigger on rms_reservations promotes it into the
 * permanent log in the same transaction as this insert, so a booking that fails
 * to write leaves no record claiming it was taken.
 */
export async function createReservation(operator, payload) {
  const config = configFor(operator);
  if (!config) return createErrorResponse("Operator has no vertical configured.");

  const customer = (await db.get("customers", payload.customer_id)).data;
  if (!customer) return createErrorResponse("Customer not found.");
  if (customer.is_blocked) {
    return createErrorResponse(`This ${config.terminology.customer.toLowerCase()} is blocked from renting.`);
  }

  const isContinuous = config.occupancyModel === OCCUPANCY_CONTINUOUS;
  const endsAt = isContinuous ? null : payload.ends_at;

  const reservations = (await db.list("reservations", { operator_id: operator.id })).data || [];
  const blocks = (await db.list("availabilityBlocks", { operator_id: operator.id })).data || [];

  // ---- resolve which physical asset is being booked -------------------------
  let asset;
  let group = null;

  if (payload.asset_group_id) {
    // Booking a class: allocate a free member rather than blocking the listing.
    group = (await db.get("assetGroups", payload.asset_group_id)).data;
    if (!group) return createErrorResponse("That listing no longer exists.");

    const groupAssets = (await db.list("assets", { operator_id: operator.id, group_id: group.id })).data || [];
    const { asset: allocated, conflict } = allocateFromGroup({
      groupAssets,
      reservations,
      blocks,
      startsAt: payload.starts_at,
      endsAt,
    });

    if (!allocated) {
      return createErrorResponse(
        conflict?.message || `No ${group.name} units are free for that window.`
      );
    }
    asset = allocated;
  } else {
    asset = (await db.get("assets", payload.asset_id)).data;
    if (!asset) return createErrorResponse("Asset not found.");
    if (asset.group_id) group = (await db.get("assetGroups", asset.group_id)).data;

    const conflict = availabilityConflict({
      asset,
      reservations,
      blocks,
      startsAt: payload.starts_at,
      endsAt,
      excludeReservationId: payload.id || null,
    });
    if (conflict) return createErrorResponse(conflict.message);
  }

  // ---- price it -------------------------------------------------------------
  const rules = (await db.list("rateRules", { operator_id: operator.id })).data || [];
  const allAssets = (await db.list("assets", { operator_id: operator.id })).data || [];

  const quote = buildQuote({
    asset,
    group,
    rules,
    startsAt: payload.starts_at,
    endsAt,
    utilizationPct: utilizationPct({ assets: allAssets, reservations, startsAt: payload.starts_at, endsAt }),
  });

  if (!quote.meets_minimum) {
    return createErrorResponse(
      `A minimum of ${quote.minimum_units} ${quote.rate_unit.replace("_", " ")}s applies to those dates.`
    );
  }

  // ---- add-ons --------------------------------------------------------------
  const available = addonsForAsset(
    (await db.list("addons", { operator_id: operator.id })).data || [],
    asset,
    group
  );

  // Required add-ons are applied automatically; the rest come from the form.
  const chosen = new Map((payload.addons || []).map((a) => [a.addon_id, a.quantity || 1]));
  available.filter((a) => a.is_required).forEach((a) => {
    if (!chosen.has(a.id)) chosen.set(a.id, a.default_quantity || 1);
  });

  let addonTotal = 0;
  const addonRows = [];
  for (const [addonId, quantity] of chosen.entries()) {
    const addon = available.find((a) => a.id === addonId);
    if (!addon) continue;
    const amount = addonAmountCents(addon, quantity, quote.units);
    addonTotal += amount;
    addonRows.push({ addon, quantity, amount });
  }

  const quotedTotal = quote.subtotal_cents + addonTotal;

  const reservation = (
    await db.create("reservations", {
      operator_id: operator.id,
      asset_id: asset.id,
      asset_group_id: group?.id || null,
      customer_id: payload.customer_id,
      location_id: asset.location_id || null,
      reservation_number: await db.nextNumber("reservations", operator.id, "RES", "reservation_number"),
      created_signature_id: payload.created_signature_id || null,
      status: payload.status || (asset.booking_mode === "instant" ? "confirmed" : "pending"),
      starts_at: payload.starts_at,
      // Continuous verticals book open-ended: a storage lease has no return date.
      ends_at: endsAt,
      rate_snapshot: quote,
      quoted_total_cents: quotedTotal,
      reservation_deposit_cents: reservationDepositCents(asset, quotedTotal),
      currency: operator.currency || "USD",
      source: payload.source || "staff",
      notes: payload.notes || null,
    })
  ).data;

  for (const { addon, quantity, amount } of addonRows) {
    await db.create("reservationAddons", {
      operator_id: operator.id,
      reservation_id: reservation.id,
      addon_id: addon.id,
      name_snapshot: addon.name,
      charge_type: addon.charge_type,
      quantity,
      unit_cents: addon.price_cents,
      amount_cents: amount,
      taxable: addon.taxable !== false,
    });
  }

  for (const answer of payload.answers || []) {
    await db.create("reservationAnswers", {
      operator_id: operator.id,
      reservation_id: reservation.id,
      question_id: answer.question_id,
      prompt_snapshot: answer.prompt,
      answer: answer.answer,
      follow_up: answer.follow_up || {},
    });
  }

  await db.update("assets", asset.id, { status: "reserved" });

  // Confirmation email. Only for a booking that is actually confirmed — a
  // request still awaiting approval must not tell the customer they are booked.
  //
  // Deliberately not awaited into the return value: a mail provider having a
  // bad afternoon must not fail a booking that is already committed to the
  // database. The failure is recorded on the message row and shows in the
  // thread, which is where someone can act on it.
  if (reservation.status === "confirmed") {
    sendTemplated({
      operatorId: operator.id,
      templateKey: "booking_confirmed",
      customer,
      reservationId: reservation.id,
      values: {
        customer_name: customer.first_name,
        business_name: operator.business_name,
        asset: assetLabel(asset),
        reservation_number: reservation.reservation_number,
        starts_at: formatWhen(reservation.starts_at),
        ends_at: reservation.ends_at ? formatWhen(reservation.ends_at) : "open-ended",
        total: formatCents(quotedTotal, operator.currency),
      },
    }).catch((err) => console.warn("Confirmation email failed:", err));
  }

  return createSuccessResponse(reservation);
}

/**
 * What moving a reservation to new dates would do — WITHOUT doing it.
 *
 * Rescheduling is the one gesture on the timeline that can change what a
 * customer is charged. A move across a weekend, into a busier fortnight, or
 * over a length-of-hire tier boundary re-prices legitimately, and per-day
 * add-ons re-price with it. An operator who drags a booking two days and does
 * not notice the total moved has a conversation with a customer they cannot
 * win, so this returns a proposal and writes nothing.
 *
 * Availability, allocation and pricing all run here rather than in the screen,
 * for the same reason they do everywhere else: the engine decides, the UI asks.
 */
export async function previewReschedule(operator, reservationId, startsAt, endsAt) {
  const config = configFor(operator);
  if (!config) return createErrorResponse("Operator has no vertical configured.");

  const reservation = (await db.get("reservations", reservationId)).data;
  if (!reservation) return createErrorResponse("That booking no longer exists.");

  const isContinuous = config.occupancyModel === OCCUPANCY_CONTINUOUS;
  const nextEnds = isContinuous ? null : endsAt;

  const assets = (await db.list("assets", { operator_id: operator.id })).data || [];
  const reservations = (await db.list("reservations", { operator_id: operator.id })).data || [];
  const blocks = (await db.list("availabilityBlocks", { operator_id: operator.id })).data || [];
  const rules = (await db.list("rateRules", { operator_id: operator.id })).data || [];

  const group = reservation.asset_group_id
    ? (await db.get("assetGroups", reservation.asset_group_id)).data
    : null;

  // A pool booking keeps belonging to the pool: the physical unit is still
  // chosen at checkout, so the question is whether ANY member is free, not
  // whether the one it happens to point at is.
  let asset = reservation.asset_id ? assets.find((a) => a.id === reservation.asset_id) : null;
  let conflict = null;

  if (group && !reservation.asset_id) {
    const groupAssets = assets.filter((a) => a.group_id === group.id);
    const allocated = allocateFromGroup({
      groupAssets, reservations, blocks, startsAt, endsAt: nextEnds,
    });
    conflict = allocated.conflict;
    asset = allocated.asset || groupAssets[0] || null;
  } else if (asset) {
    conflict = availabilityConflict({
      asset, reservations, blocks, startsAt, endsAt: nextEnds,
      // Its own current dates must not block its new ones.
      excludeReservationId: reservation.id,
    });
  }

  if (!asset) return createErrorResponse("That booking has nothing to move.");

  const quote = quoteReservationSync({
    asset,
    group,
    rules,
    startsAt,
    endsAt: nextEnds,
    utilization: utilizationPct({ assets, reservations, startsAt, endsAt: nextEnds }),
  });

  // Add-ons move with the booking, and a per-day add-on costs a different
  // amount over a different number of days. Re-priced from the rows already on
  // the reservation rather than from the operator's current add-on list, so a
  // price change since booking does not ride in on a date change.
  const addonRows = (await db.list("reservationAddons", { reservation_id: reservation.id })).data || [];
  const addonTotal = addonRows.reduce(
    (sum, row) =>
      sum +
      addonAmountCents(
        { charge_type: row.charge_type, price_cents: row.unit_cents },
        row.quantity,
        quote.units
      ),
    0
  );

  const nextTotal = quote.subtotal_cents + addonTotal;

  return createSuccessResponse({
    reservation,
    asset,
    group,
    conflict,
    blocked: !!conflict || !quote.meets_minimum,
    quote,
    addonRows,
    addonTotal,
    before: {
      startsAt: reservation.starts_at,
      endsAt: reservation.ends_at,
      totalCents: reservation.quoted_total_cents || 0,
    },
    after: { startsAt, endsAt: nextEnds, totalCents: nextTotal },
  });
}

/**
 * Commits a move that previewReschedule proposed.
 *
 * Re-previews rather than trusting the caller's numbers: the operator may have
 * left the dialog open while somebody else took a booking, and the engine must
 * have the last word on whether the dates are still free.
 */
export async function applyReschedule(operator, reservationId, startsAt, endsAt) {
  const preview = await previewReschedule(operator, reservationId, startsAt, endsAt);
  if (!preview.success) return preview;

  const { conflict, quote, addonRows, after } = preview.data;
  if (conflict) return createErrorResponse(conflict.message);
  if (!quote.meets_minimum) {
    return createErrorResponse(
      `A minimum of ${quote.minimum_units} ${quote.rate_unit.replace("_", " ")}s applies to those dates.`
    );
  }

  const updated = await db.update("reservations", reservationId, {
    starts_at: after.startsAt,
    ends_at: after.endsAt,
    rate_snapshot: quote,
    quoted_total_cents: after.totalCents,
  });

  // The add-on rows carry their own amounts, and a per-day add-on over a
  // different number of days is a different amount. Left stale they would
  // disagree with the total on the same screen.
  for (const row of addonRows) {
    const amount = addonAmountCents(
      { charge_type: row.charge_type, price_cents: row.unit_cents },
      row.quantity,
      quote.units
    );
    if (amount !== row.amount_cents) {
      await db.update("reservationAddons", row.id, { amount_cents: amount });
    }
  }

  return updated;
}

/**
 * Edits a booking that has not gone out yet.
 *
 * Dates are deliberately NOT changed here. They re-price, and re-pricing has to
 * be shown before it is committed -- that is what previewReschedule and
 * applyReschedule exist for. Letting a general-purpose update quietly move the
 * dates would route around the one dialog standing between an operator and a
 * conversation with a customer about a total that changed by itself.
 *
 * What is here is everything that does NOT touch the money: who it is for,
 * where it is going, and what the operator wrote on it.
 */
export async function updateReservation(reservationId, patch) {
  const reservation = (await db.get("reservations", reservationId)).data;
  if (!reservation) return createErrorResponse("Reservation not found.");
  if (["completed", "closed", "cancelled", "no_show"].includes(reservation.status)) {
    return createErrorResponse("That booking is finished and can no longer be edited.");
  }

  const allowed = {};
  if (patch.customer_id !== undefined) {
    const customer = (await db.get("customers", patch.customer_id)).data;
    if (!customer) return createErrorResponse("Customer not found.");
    if (customer.is_blocked) return createErrorResponse("That customer is blocked from renting.");
    allowed.customer_id = patch.customer_id;
  }
  if (patch.notes !== undefined) allowed.notes = patch.notes || null;
  if (patch.location_id !== undefined) allowed.location_id = patch.location_id || null;

  if (!Object.keys(allowed).length) return createSuccessResponse(reservation);
  return db.update("reservations", reservationId, allowed);
}

export async function cancelReservation(reservationId) {
  const reservation = (await db.get("reservations", reservationId)).data;
  if (!reservation) return createErrorResponse("Reservation not found.");
  if (["checked_out", "active"].includes(reservation.status)) {
    return createErrorResponse("Check the rental back in before cancelling it.");
  }

  await db.update("reservations", reservationId, { status: "cancelled" });
  await db.update("assets", reservation.asset_id, { status: "available" });
  return createSuccessResponse(true);
}

// ----------------------------------------------------------------------------
// Checkout — the moment the asset leaves
// ----------------------------------------------------------------------------

/**
 * Pre-flight gate. Returns the list of blocking reasons so the UI can explain
 * exactly what is missing rather than failing with one generic message.
 */
export async function checkoutBlockers(operator, reservation, asset, customer) {
  const config = configFor(operator);
  const blockers = [];
  const w = config.workflow;

  if (customer.is_blocked) blockers.push("This customer is blocked from renting.");

  if (w.idVerificationRequired && !customer.id_verification?.verified_at) {
    blockers.push("Customer ID has not been verified.");
  }

  // Construction equipment: an uncertified operator must not be handed a lift.
  if (w.certificationRequired) {
    const required = asset.custom_fields?.required_certification;
    const ok = await db.hasValidCertification(operator.id, customer.id, required);
    if (!ok) {
      blockers.push(
        `A verified, unexpired "${required}" certification is required for ${asset.name}.`
      );
    }
  }

  if (w.insuranceCertificateRequired && !customer.id_verification?.insurance_on_file) {
    blockers.push("A certificate of insurance is required and is not on file.");
  }

  return blockers;
}

/** Everything checkOut refuses for. See validateCheckIn for why this exists. */
export async function validateCheckOut(operator, { reservationId, signaturePath, waiverAccepted }) {
  const config = configFor(operator);
  const reservation = (await db.get("reservations", reservationId)).data;
  if (!reservation) return createErrorResponse("Reservation not found.");
  if (reservation.status === "checked_out" || reservation.status === "active") {
    return createErrorResponse("This rental is already out.");
  }

  const asset = (await db.get("assets", reservation.asset_id)).data;
  const customer = (await db.get("customers", reservation.customer_id)).data;

  const blockers = await checkoutBlockers(operator, reservation, asset, customer);
  if (blockers.length) return createErrorResponse(blockers.join(" "));

  const w = config.workflow;
  if (w.waiverRequired && !waiverAccepted) {
    return createErrorResponse("The liability waiver must be accepted before checkout.");
  }
  if (w.signatureRequiredAtCheckout && !signaturePath) {
    return createErrorResponse("A customer signature is required at checkout.");
  }
  return createSuccessResponse(true);
}

export async function checkOut(operator, { reservationId, capturedFields, signaturePath, waiverAccepted, notes, signatureId, waiverVersion }) {
  const config = configFor(operator);

  const pre = await validateCheckOut(operator, { reservationId, signaturePath, waiverAccepted });
  if (!pre.success) return pre;

  const reservation = (await db.get("reservations", reservationId)).data;
  const asset = (await db.get("assets", reservation.asset_id)).data;
  const customer = (await db.get("customers", reservation.customer_id)).data;
  const w = config.workflow;

  const isContinuous = config.occupancyModel === OCCUPANCY_CONTINUOUS;

  const agreement = (
    await db.create("agreements", {
      operator_id: operator.id,
      reservation_id: reservation.id,
      asset_id: asset.id,
      customer_id: customer.id,
      agreement_number: await db.nextNumber("agreements", operator.id, "AGR", "agreement_number"),
      status: "open",
      checkout_at: nowIso(),
      checkout_data: capturedFields || {},
      checkout_signature_path: signaturePath || null,
      // Which EMPLOYEE released this, as opposed to which account was signed
      // in -- on a shared counter terminal those are different questions. A
      // trigger on rms_agreements refuses the insert unless this was minted
      // seconds ago by rms_sign_action for this operator.
      checkout_signature_id: signatureId || null,
      deposit_cents: w.depositRequired ? asset.deposit_cents || 0 : 0,
      deposit_status: w.depositRequired && asset.deposit_cents ? "held" : "none",
      waiver_accepted_at: waiverAccepted ? nowIso() : null,
      // Held the string literal "v1" from 20260816100000 until 20260821170000,
      // which meant the record could say a waiver was accepted and never which
      // one. It now holds the rms_agreement_documents id whose body_snapshot is
      // the exact text that was agreed to. Still "v1" where an operator has no
      // agreement set up yet, because that is honestly all we know.
      waiver_version: waiverAccepted ? waiverVersion || "v1" : null,
      notes: notes || null,
    })
  ).data;

  await recordUsageReadings(operator, asset, agreement.id, capturedFields, "checkout");

  // The deposit is the customer's money from the instant it is taken. Posting
  // it as a liability here rather than leaving it unrecorded is what stops a
  // month of held deposits reading as a good month.
  if (agreement?.deposit_cents) {
    await glService.postDepositHeld(operator, {
      agreementId: agreement.id,
      customerId: reservation.customer_id,
      amountCents: agreement.deposit_cents,
      at: agreement.checkout_at,
    });
  }

  await db.update("reservations", reservation.id, {
    status: isContinuous ? "active" : "checked_out",
  });
  await db.update("assets", asset.id, {
    status: isContinuous ? "occupied" : "rented",
    custom_fields: { ...asset.custom_fields, ...(capturedFields || {}) },
  });

  // Storage: opening an occupancy and issuing access credentials replaces the
  // "asset is out on a trip" concept entirely.
  if (isContinuous) {
    await openOccupancy(operator, { reservation, asset, customer, agreement, capturedFields });
  }

  return createSuccessResponse(agreement);
}

// ----------------------------------------------------------------------------
// Check-in — the moment it comes back
// ----------------------------------------------------------------------------

/**
 * Everything checkIn refuses for, decided before anything is written.
 *
 * Extracted so a screen can ask BEFORE collecting a PIN. rms_sign_action writes
 * a row to rms_staff_actions, which is append-only by design -- it has no
 * UPDATE or DELETE policy at all. So minting a signature and only then
 * discovering the operation was never permitted leaves the log permanently
 * asserting that somebody closed an agreement they did not close, and no way to
 * withdraw it. For a log whose whole job is answering "who did what", entries
 * for things nobody did are the one kind of wrong it cannot absorb.
 *
 * checkIn calls this itself as well, so the screen skipping it changes nothing
 * about what the database accepts.
 */
export async function validateCheckIn(operator, { agreementId, signaturePath }) {
  const config = configFor(operator);
  const agreement = (await db.get("agreements", agreementId)).data;
  if (!agreement) return createErrorResponse("Agreement not found.");
  if (agreement.status !== "open") return createErrorResponse("This agreement is already closed.");
  if (config.workflow.signatureRequiredAtCheckin && !signaturePath) {
    return createErrorResponse("A signature is required at check-in.");
  }
  return createSuccessResponse(true);
}

export async function checkIn(operator, { agreementId, capturedFields, signaturePath, damageFound, damageNotes, damageChargeCents, notes, signatureId }) {
  const config = configFor(operator);

  const pre = await validateCheckIn(operator, { agreementId, signaturePath });
  if (!pre.success) return pre;

  const agreement = (await db.get("agreements", agreementId)).data;
  const asset = (await db.get("assets", agreement.asset_id)).data;
  const w = config.workflow;

  await db.update("agreements", agreementId, {
    status: "closed",
    checkin_at: nowIso(),
    checkin_data: capturedFields || {},
    checkin_signature_path: signaturePath || null,
    checkin_signature_id: signatureId || null,
    deposit_status: agreement.deposit_cents
      ? damageFound && damageChargeCents >= agreement.deposit_cents
        ? "forfeited"
        : damageFound
        ? "partial"
        : "released"
      : "none",
    notes: notes || agreement.notes,
  });

  await recordUsageReadings(operator, asset, agreementId, capturedFields, "checkin");

  if (w.conditionReportRequired || damageFound) {
    await db.create("conditionReports", {
      operator_id: operator.id,
      agreement_id: agreementId,
      asset_id: asset.id,
      phase: "checkin",
      items: capturedFields?.checklist || [],
      photos: capturedFields?.photos || [],
      damage_found: !!damageFound,
      damage_notes: damageNotes || null,
      charge_cents: damageChargeCents || 0,
      inspected_at: nowIso(),
    });
  }

  if (agreement.reservation_id) {
    await db.update("reservations", agreement.reservation_id, { status: "completed" });
  }

  // Items that must be cleaned or repaired before re-renting do not go straight
  // back to "available" — that is how a dirty tent gets rented out twice.
  const needsService =
    damageFound ||
    (w.cleaningCycleRequired &&
      ["Needs cleaning", "Needs drying", "Needs repair", "At cleaner"].includes(
        capturedFields?.cleaning_status
      ));

  await db.update("assets", asset.id, {
    status: needsService ? "maintenance" : "available",
    custom_fields: { ...asset.custom_fields, ...(capturedFields || {}) },
  });

  const invoice = await generateInvoiceForAgreement(operator, agreementId, damageChargeCents);

  // Clear the deposit liability, AFTER the invoice exists so there is a
  // receivable for it to settle.
  //
  // The damage charge went onto that invoice as a `damage` line, which already
  // booked it as revenue and raised the receivable. So the deposit PAYS the
  // receivable -- it is not income. Crediting it to revenue as well would
  // record one dented bumper twice: once when it was billed, once when the
  // deposit covered the bill.
  //
  // forfeitedToRevenueCents stays zero on this path for exactly that reason. It
  // exists for deposit money kept where nothing was invoiced -- a no-show, a
  // cancellation absorbed by the deposit -- which does not happen here.
  if (agreement.deposit_cents) {
    await glService.postDepositResolved(operator, {
      agreementId,
      customerId: agreement.customer_id,
      heldCents: agreement.deposit_cents,
      appliedToInvoiceCents: damageFound
        ? Math.min(damageChargeCents || 0, agreement.deposit_cents)
        : 0,
      forfeitedToRevenueCents: 0,
      at: nowIso(),
    });
  }

  return createSuccessResponse(invoice);
}

// ----------------------------------------------------------------------------
// Usage metering
// ----------------------------------------------------------------------------

async function recordUsageReadings(operator, asset, agreementId, capturedFields, phase) {
  const config = configFor(operator);
  for (const metric of config.usageMetrics || []) {
    const value = capturedFields?.[metric.key];
    if (value === undefined || value === null || value === "") continue;
    await db.create("usageReadings", {
      operator_id: operator.id,
      asset_id: asset.id,
      agreement_id: agreementId,
      reading_type: metric.key,
      phase,
      value: Number(value),
      unit: metric.unit,
      recorded_at: nowIso(),
    });
  }
}

/**
 * Overage lines for metered usage.
 *
 * Prefers the operator's own usage-fee schedule for a metric — named, tiered
 * and editable — and falls back to the vertical preset's constants only where
 * no schedule has been set up.
 */
export async function usageOverageLines(operator, agreement, taxConfig = null) {
  const config = configFor(operator);
  const tax = taxConfig || (await loadTaxConfig(operator));
  const asset = (await db.get("assets", agreement.asset_id)).data;
  const schedules = (await db.list("usageFeeSchedules", { operator_id: operator.id })).data || [];

  const lines = [];
  const days = Math.max(
    1,
    Math.ceil(
      (new Date(agreement.checkin_at || nowIso()).getTime() -
        new Date(agreement.checkout_at).getTime()) /
        864e5
    )
  );

  for (const metric of config.usageMetrics || []) {
    const out = Number(agreement.checkout_data?.[metric.key]);
    const back = Number(agreement.checkin_data?.[metric.key]);
    if (!Number.isFinite(out) || !Number.isFinite(back) || back <= out) continue;
    const used = back - out;

    // The asset may pin a specific schedule (e.g. "Unlimited mileage" as a
    // paid upgrade); otherwise take the first active one for this metric.
    const pinnedId = asset?.usage_fee_schedules?.[metric.key];
    const schedule =
      schedules.find((s) => s.id === pinnedId) ||
      schedules.find((s) => s.metric_key === metric.key && s.is_active !== false);

    if (schedule) {
      const { overage, included, cents } = usageChargeCents(schedule, used, days);
      if (overage <= 0 || cents <= 0) continue;
      lines.push({
        kind: "usage_overage",
        description: `${metric.label} overage — ${overage.toLocaleString(getLocale())} ${metric.unit} beyond ${Number(included).toLocaleString(getLocale())} included (${schedule.name})`,
        quantity: overage,
        unit_cents: schedule.flat_cents_per_unit || 0,
        amount_cents: cents,
        ...taxOn(tax, "usage_overage"),
      });
      continue;
    }

    if (!metric.billable) continue;
    const included = (metric.includedPerDay || 0) * days;
    const over = used - included;
    if (over <= 0) continue;

    lines.push({
      kind: "usage_overage",
      description: `${metric.label} overage — ${over.toLocaleString(getLocale())} ${metric.unit} beyond ${included.toLocaleString(getLocale())} included`,
      quantity: over,
      unit_cents: metric.overageCentsPerUnit || 0,
      amount_cents: Math.round(over * (metric.overageCentsPerUnit || 0)),
      ...taxOn(tax, "usage_overage"),
    });
  }
  return lines;
}

// ----------------------------------------------------------------------------
// Invoicing
// ----------------------------------------------------------------------------

export async function generateInvoiceForAgreement(operator, agreementId, damageChargeCents = 0) {
  const agreement = (await db.get("agreements", agreementId)).data;
  const reservation = agreement.reservation_id
    ? (await db.get("reservations", agreement.reservation_id)).data
    : null;
  const asset = (await db.get("assets", agreement.asset_id)).data;

  const taxConfig = await loadTaxConfig(operator);
  const lines = [];

  // The quote agreed at booking time is authoritative — re-pricing at check-in
  // would let a rate change after the fact rewrite what the customer accepted.
  const quote =
    reservation?.rate_snapshot ||
    (await quoteReservation(operator, asset, agreement.checkout_at, agreement.checkin_at));

  lines.push({
    kind: "rental",
    // The code goes in the description, not just the name. line_items is a
    // snapshot: once written it is never recalculated, so an invoice raised
    // today without the unit number can never be matched back to a specific
    // machine, however carefully anyone reconciles later.
    description: `${assetLabel(asset)} — ${quote.units} × ${quote.rate_unit.replace("_", " ")}`,
    quantity: quote.units,
    unit_cents: quote.unit_cents,
    amount_cents: quote.base_cents ?? quote.subtotal_cents,
    ...taxOn(taxConfig, "rental"),
  });

  const rentalGroupId = taxConfig.groupFor("rental");

  // Each rate rule that fired becomes its own visible line, so a customer
  // asking "why is it $340?" gets an itemised answer.
  for (const rule of quote.rule_lines || []) {
    lines.push({
      kind: rule.deltaCents < 0 ? "discount" : "surcharge",
      description: rule.name,
      quantity: 1,
      unit_cents: rule.deltaCents,
      amount_cents: rule.deltaCents,
      // A discount or surcharge is an adjustment to the RENTAL price, so it has
      // to reduce or inflate the same tax base it moved. Letting it fall to its
      // own category default would produce an invoice that adds up perfectly
      // and is legally wrong.
      tax_group_id: rentalGroupId,
      taxable: rentalGroupId !== null,
    });
  }

  // Add-ons chosen at booking, at their snapshotted prices.
  if (agreement.reservation_id) {
    const addons =
      (await db.list("reservationAddons", { operator_id: operator.id, reservation_id: agreement.reservation_id })).data || [];
    for (const a of addons) {
      lines.push({
        kind: "addon",
        description: a.quantity > 1 ? `${a.name_snapshot} × ${a.quantity}` : a.name_snapshot,
        quantity: a.quantity,
        unit_cents: a.unit_cents,
        amount_cents: a.amount_cents,
        // The add-on's own taxable flag is real operator data and still wins:
        // an add-on marked non-taxable is not taxed whatever the category says.
        ...(a.taxable === false
          ? { tax_group_id: null, taxable: false }
          : taxOn(taxConfig, "addon")),
      });
    }
  }

  lines.push(...(await usageOverageLines(operator, agreement, taxConfig)));

  if (damageChargeCents > 0) {
    lines.push({
      kind: "damage",
      description: "Damage / repair charge",
      quantity: 1,
      unit_cents: damageChargeCents,
      amount_cents: damageChargeCents,
      ...taxOn(taxConfig, "damage"),
    });
  }

  const deliveries = (await db.list("deliveries", { operator_id: operator.id, reservation_id: agreement.reservation_id })).data || [];
  const deliveryFee = deliveries.reduce((sum, d) => sum + (d.fee_cents || 0), 0);
  if (deliveryFee > 0) {
    lines.push({
      kind: "delivery",
      description: `Delivery & pickup (${deliveries.length} leg${deliveries.length === 1 ? "" : "s"})`,
      quantity: 1,
      unit_cents: deliveryFee,
      amount_cents: deliveryFee,
      // Delivery is a service and is frequently taxed differently from the
      // goods being hired -- which is the whole reason category defaults exist.
      ...taxOn(taxConfig, "delivery"),
    });
  }

  return finalizeInvoice(operator, {
    customer_id: agreement.customer_id,
    agreement_id: agreementId,
    line_items: lines,
    taxConfig,
  });
}

/**
 * Totals a line-item set and writes the invoice.
 *
 * Tax is computed from the operator's LIVE configuration, deliberately, and not
 * from the quote snapshotted at booking. Everything else here is snapshot
 * discipline -- line_items, once written, is never recalculated -- so the
 * exception needs saying out loud:
 *
 *   The price is a contract as of booking. The tax is a legal obligation as of
 *   the tax point, which is the invoice date.
 *
 * If a rate moved between booking and check-in, the invoice must carry the new
 * one. In tax-inclusive mode the gross was fixed by the quote, so a rate change
 * moves the NET and the operator absorbs or gains the difference -- which is
 * exactly how inclusive pricing works everywhere it is used.
 *
 * `taxConfig` may be passed in by callers that raise many invoices in a loop,
 * so the configuration is loaded once rather than per invoice.
 */
export async function finalizeInvoice(operator, { customer_id, agreement_id = null, subscription_id = null, line_items, period_start = null, period_end = null, taxConfig = null }) {
  const config = taxConfig || (await loadTaxConfig(operator));

  /**
   * Decide a tax group for any line that did not arrive with one.
   *
   * applyTax reads tax_group_id off the line and does NOT derive it from the
   * kind, so a caller who passes `{ kind: "rental" }` and nothing else gets a
   * silently untaxed invoice -- no error, no warning, a total that is simply
   * short by the tax. Both existing callers stamp it correctly, so nothing was
   * ever wrong in production; the trap was for the next one, and it caught me
   * twice while testing the ledger.
   *
   * The distinction is between a line that has DECIDED and one that has not.
   * `"tax_group_id" in line` is deliberate rather than a truthiness check: an
   * explicit null means exempt and must be honoured, and `?? undefined` would
   * quietly overwrite it with the category default.
   *
   * Resolved here rather than inside applyTax because applyTax is the pricing
   * engine and takes no config beyond rates -- teaching it about line kinds
   * and category defaults would give it a second job and a reason to know
   * about the rest of the application.
   */
  const lines = (line_items || []).map((line) => {
    if ("tax_group_id" in line) return line;
    if (line.taxable === false) return { ...line, tax_group_id: null };
    return { ...line, ...taxOn(config, line.kind) };
  });

  const priced = applyTax(lines, config);

  const invoice = (
    await db.create("invoices", {
      operator_id: operator.id,
      customer_id,
      agreement_id,
      subscription_id,
      invoice_number: await db.nextNumber("invoices", operator.id, "INV", "invoice_number"),
      status: "sent",
      line_items: priced.lines,
      subtotal_cents: priced.subtotal_cents,
      discount_cents: 0,
      tax_cents: priced.tax_cents,
      total_cents: priced.total_cents,
      amount_paid_cents: 0,
      currency: operator.currency || "USD",
      // Deprecated, written for backwards compatibility and never read. One
      // blended number cannot express four components, and basis points cannot
      // express 9.975% -- tax_breakdown is the real record.
      tax_rate_bps: priced.subtotal_cents > 0
        ? Math.round((priced.tax_cents * 10000) / priced.subtotal_cents)
        : 0,
      tax_breakdown: priced.tax_breakdown,
      period_start,
      period_end,
      issued_at: nowIso(),
      due_at: new Date(Date.now() + 14 * 864e5).toISOString(),
    })
  ).data;

  // Accrual: the receivable and the revenue exist the moment the invoice does,
  // not when it is paid. Failure is swallowed for the same reason as in
  // recordPayment -- the invoice is the document, the ledger is derived from
  // it, and a customer is not left without a bill because a posting failed.
  if (invoice) await glService.postInvoice(operator, invoice);

  return invoice;
}

/**
 * `signatureId` is a PENDING signature for 'payment.take', spent by the insert
 * below. Money moving is the thing most worth being able to attribute, and
 * created_by cannot do it -- it holds the auth account, which at a counter used
 * by a whole shift is the same answer every time.
 *
 * A NEGATIVE amount is a refund. It takes the same signature class; what stops
 * a staff member issuing one is the RESTRICTIVE policy on rms_payments, not the
 * signature, so the rule about who may reverse money lives in exactly one place.
 */
export async function recordPayment(
  operator,
  { invoiceId, amountCents, method = "card", reference = null, signatureId = null }
) {
  const invoice = (await db.get("invoices", invoiceId)).data;
  if (!invoice) return createErrorResponse("Invoice not found.");
  if (invoice.status === "void") return createErrorResponse("This invoice has been voided.");

  const payment = await db.create("payments", {
    operator_id: operator.id,
    invoice_id: invoiceId,
    customer_id: invoice.customer_id,
    amount_cents: amountCents,
    currency: invoice.currency,
    method,
    status: "captured",
    reference,
    captured_at: nowIso(),
    created_signature_id: signatureId,
  });

  // The insert is what spends the signature, so a refusal here means nothing
  // was signed either -- the pending row is still unspent and the log is silent.
  if (!payment.success) return payment;

  // Posted after the payment exists, and its failure is deliberately not
  // returned: the payment is the operational record and the customer is not
  // made to wait at a counter because a ledger write failed. source_id says
  // precisely which payments have been posted, so a gap can be filled later.
  if (payment.success && payment.data) {
    await glService.postPayment(operator, {
      ...payment.data,
      received_at: payment.data.captured_at,
    });
  }

  // The local adapter has no trigger, so recompute paid state here. The
  // Supabase path gets the same result from trg_rms_payments_sync_invoice.
  const payments = (await db.list("payments", { operator_id: operator.id, invoice_id: invoiceId })).data || [];
  const paid = payments.filter((p) => p.status === "captured").reduce((s, p) => s + p.amount_cents, 0);

  const updated = (
    await db.update("invoices", invoiceId, {
      amount_paid_cents: paid,
      status: paid >= invoice.total_cents ? "paid" : paid > 0 ? "partially_paid" : invoice.status,
      paid_at: paid >= invoice.total_cents ? nowIso() : null,
    })
  ).data;

  return createSuccessResponse(updated);
}

// ----------------------------------------------------------------------------
// Continuous occupancy (storage lockers)
// ----------------------------------------------------------------------------

async function openOccupancy(operator, { reservation, asset, customer, agreement, capturedFields }) {
  const config = configFor(operator);

  const subscription = (
    await db.create("subscriptions", {
      operator_id: operator.id,
      customer_id: customer.id,
      asset_id: asset.id,
      reservation_id: reservation.id,
      status: "active",
      billing_interval: "month",
      amount_cents: asset.base_rate_cents || 0,
      currency: operator.currency || "USD",
      start_date: nowIso().slice(0, 10),
      next_bill_date: nextBillDate(new Date()),
      prorate_first_period: config.rateDefaults?.prorateFirstMonth !== false,
      late_fee_cents: config.rateDefaults?.lateFeeCents || 0,
      late_fee_grace_days: config.rateDefaults?.lateFeeGraceDays ?? 5,
      lien_status: "none",
    })
  ).data;

  const occupancy = (
    await db.create("occupancies", {
      operator_id: operator.id,
      asset_id: asset.id,
      customer_id: customer.id,
      subscription_id: subscription.id,
      agreement_id: agreement.id,
      status: "active",
      moved_in_at: nowIso(),
      lock_type: capturedFields?.lock_type || "tenant_padlock",
    })
  ).data;

  // Issue the gate code recorded on the unit.
  const code = capturedFields?.access_code || asset.custom_fields?.access_code;
  if (code) {
    await db.create("accessCredentials", {
      operator_id: operator.id,
      occupancy_id: occupancy.id,
      asset_id: asset.id,
      customer_id: customer.id,
      credential_type: "gate_code",
      credential_value: String(code),
      label: "Facility gate code",
      issued_at: nowIso(),
    });
  }

  return occupancy;
}

export async function moveOut(operator, { occupancyId, unitCondition, notes }) {
  const occupancy = (await db.get("occupancies", occupancyId)).data;
  if (!occupancy) return createErrorResponse("Occupancy not found.");
  if (occupancy.status === "ended") return createErrorResponse("This tenant has already moved out.");

  await db.update("occupancies", occupancyId, {
    status: "ended",
    moved_out_at: nowIso(),
    move_out_notes: notes || null,
  });

  // Mirrors trg_rms_occupancy_move_out: revoke every live credential and free
  // the unit. A working gate code after move-out is a real security problem.
  const creds = (await db.list("accessCredentials", { operator_id: operator.id, occupancy_id: occupancyId })).data || [];
  for (const cred of creds.filter((c) => !c.revoked_at)) {
    await db.update("accessCredentials", cred.id, {
      revoked_at: nowIso(),
      revoked_reason: "occupancy_ended",
    });
  }

  if (occupancy.subscription_id) {
    await db.update("subscriptions", occupancy.subscription_id, {
      status: "ended",
      end_date: nowIso().slice(0, 10),
    });
  }

  await db.update("assets", occupancy.asset_id, {
    status: unitCondition === "needs_cleaning" ? "maintenance" : "available",
  });

  if (occupancy.agreement_id) {
    await db.update("agreements", occupancy.agreement_id, {
      status: "closed",
      checkin_at: nowIso(),
      deposit_status: "released",
    });
  }

  return createSuccessResponse(true);
}

export async function logAccessEvent(operator, { assetId, occupancyId, customerId, credentialId, eventType, denialReason, source = "manual" }) {
  const event = (
    await db.create("accessEvents", {
      operator_id: operator.id,
      asset_id: assetId || null,
      occupancy_id: occupancyId || null,
      customer_id: customerId || null,
      credential_id: credentialId || null,
      event_type: eventType,
      denial_reason: denialReason || null,
      source,
      occurred_at: nowIso(),
    })
  ).data;

  if (credentialId && eventType === "entry") {
    await db.update("accessCredentials", credentialId, { last_used_at: nowIso() });
  }
  return createSuccessResponse(event);
}

function nextBillDate(from) {
  const d = new Date(from);
  d.setMonth(d.getMonth() + 1, 1);
  return d.toISOString().slice(0, 10);
}

/**
 * Recurring billing run. Raises one invoice per due subscription and advances
 * next_bill_date. Idempotent per period, matching the uq_rms_invoice_sub_period
 * index — running it twice in a day cannot double-bill a tenant.
 */
export async function runRecurringBilling(operator, asOf = new Date()) {
  const today = asOf.toISOString().slice(0, 10);
  const subs = (await db.list("subscriptions", { operator_id: operator.id, status: ["active", "past_due"] })).data || [];
  const due = subs.filter((s) => s.next_bill_date <= today);

  // Loaded once for the whole run, not per subscription: a storage yard with
  // three hundred units would otherwise re-read the same four tables three
  // hundred times.
  const taxConfig = await loadTaxConfig(operator);

  const created = [];
  for (const sub of due) {
    const periodStart = sub.next_bill_date;
    const existing = (await db.list("invoices", { operator_id: operator.id, subscription_id: sub.id })).data || [];
    if (existing.some((i) => i.period_start === periodStart && i.status !== "void")) continue;

    const asset = (await db.get("assets", sub.asset_id)).data;
    const periodEnd = nextBillDate(new Date(periodStart));

    const invoice = await finalizeInvoice(operator, {
      customer_id: sub.customer_id,
      subscription_id: sub.id,
      period_start: periodStart,
      period_end: periodEnd,
      taxConfig,
      line_items: [
        {
          kind: "rental",
          description: `${assetLabel(asset, "Unit")} — monthly rent ${periodStart} to ${periodEnd}`,
          quantity: 1,
          unit_cents: sub.amount_cents,
          amount_cents: sub.amount_cents,
          ...taxOn(taxConfig, "rental"),
        },
      ],
    });

    await db.update("subscriptions", sub.id, {
      last_billed_at: nowIso(),
      next_bill_date: periodEnd,
    });
    created.push(invoice);
  }

  return createSuccessResponse(created);
}

// ----------------------------------------------------------------------------
// Deliveries (construction equipment, party equipment)
// ----------------------------------------------------------------------------

export async function scheduleDelivery(operator, payload) {
  const config = configFor(operator);
  if (!config.workflow.deliveryEnabled) {
    return createErrorResponse("Delivery is not enabled for this business type.");
  }
  if (!payload.address_line1) {
    return createErrorResponse("A delivery address is required.");
  }

  const delivery = (
    await db.create("deliveries", {
      operator_id: operator.id,
      reservation_id: payload.reservation_id || null,
      agreement_id: payload.agreement_id || null,
      asset_id: payload.asset_id,
      direction: payload.direction || "delivery",
      status: "scheduled",
      scheduled_at: payload.scheduled_at,
      window_start: payload.window_start || null,
      window_end: payload.window_end || null,
      site_name: payload.site_name || null,
      address_line1: payload.address_line1,
      address_line2: payload.address_line2 || null,
      city: payload.city || null,
      region: payload.region || null,
      postal_code: payload.postal_code || null,
      site_contact_name: payload.site_contact_name || null,
      site_contact_phone: payload.site_contact_phone || null,
      site_notes: payload.site_notes || null,
      transport_class: payload.transport_class || null,
      permit_required: !!payload.permit_required,
      fee_cents: payload.fee_cents || 0,
    })
  ).data;

  return createSuccessResponse(delivery);
}

const rentalService = {
  configFor,
  billableUnits,
  quoteReservation,
  quoteReservationSync,
  rateEngine,
  availability,
  createReservation,
  previewReschedule,
  applyReschedule,
  updateReservation,
  cancelReservation,
  checkoutBlockers,
  checkOut,
  checkIn,
  validateCheckOut,
  validateCheckIn,
  usageOverageLines,
  generateInvoiceForAgreement,
  finalizeInvoice,
  recordPayment,
  moveOut,
  logAccessEvent,
  runRecurringBilling,
  scheduleDelivery,
};

export default rentalService;
